import { Switch as HeadlessSwitch } from "@headlessui/react";
import { type ReactNode, forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { BaseButton, type BaseButtonProps } from "./buttons/BaseButton";

import type { PolymorphicRef } from "types";

type SwitchProps = {
  checked?: boolean;
  disabled?: boolean;
  className?: string;
  onChange?: (checked: boolean) => void;
};

// Used in buttons
export function Switch({ checked, className, disabled, onChange }: SwitchProps) {
  return (
    <HeadlessSwitch
      as="div"
      checked={checked}
      className={twMerge(
        "group flex h-3.5 w-7 min-w-7 cursor-pointer items-center rounded-full bg-black-tint-70 transition data-[checked]:bg-blue data-[checked]:data-[disabled]:bg-black-tint-70",
        className,
      )}
      disabled={disabled}
      onChange={onChange}
    >
      <span className="size-3 translate-x-[0.0675rem] rounded-full bg-white shadow-md transition group-data-[checked]:translate-x-[0.9375rem]" />
    </HeadlessSwitch>
  );
}

type ToggleSwitchProps<T extends React.ElementType> = BaseButtonProps<T> & {
  isSelected?: boolean;
  labelStart?: ReactNode;
  labelEnd?: ReactNode;
  switchClassName?: string;
};

// Used as a standalone component or part of a menu item
export const ToggleSwitch = forwardRef<HTMLButtonElement, Omit<ToggleSwitchProps<"button">, "ref">>(
  function ToggleSwitchInternal(
    {
      className,
      switchClassName,
      isSelected,
      disabled,
      labelStart,
      labelEnd,
      ...rest
    }: ToggleSwitchProps<"button">,
    ref,
  ) {
    const switchElement = (
      <Switch checked={isSelected} className={switchClassName} disabled={disabled} />
    );

    return (
      <BaseButton
        className={twMerge("flex items-center gap-2 transition", className)}
        disabled={disabled}
        eventProps={{ isEnabled: !isSelected }}
        isSelected={isSelected}
        ref={ref}
        {...rest}
      >
        {labelStart}
        {switchElement}
        {labelEnd}
      </BaseButton>
    );
  },
) as <T extends React.ElementType = "button">(
  props: ToggleSwitchProps<T> & { ref?: PolymorphicRef<T> },
) => React.ReactNode;
